/**
 * Whether Synop-internal admin tooling is shown on customer-facing pages.
 *
 * Internal Admin surfaces (today: the Internal Admin tab on every Link detail
 * page) are hidden by default so a demo, a screen share or a screenshot from
 * a customer's point of view does not carry staff-only controls. Staff switch
 * them on and off from the profile menu or the command palette (Cmd+K); one
 * switch covers every surface that reads this flag, so the pattern extends
 * beyond Links.
 *
 * The preference is per user and lives in `localStorage`: some Synop users
 * want the tools always on and others always off, so it has to survive a
 * refresh, a new tab and a browser restart. Keying it by user id keeps one
 * person's choice from following whoever signs in next on a shared browser.
 * Reading it grants nothing — the surfaces it reveals still gate themselves on
 * `synop_internal_only`, and every endpoint authorizes off the JWT.
 */

const STORAGE_KEY = 'synop_internal_admin_tools'

/** User id → whether that user has the tools switched on. */
export type InternalAdminToolsPreferences = Readonly<Record<string, boolean>>

const EMPTY: InternalAdminToolsPreferences = {}

function readFromStorage(): InternalAdminToolsPreferences {
  if (typeof window === 'undefined') return EMPTY
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return EMPTY
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return EMPTY
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        (entry): entry is [string, boolean] => typeof entry[1] === 'boolean'
      )
    )
  } catch {
    return EMPTY
  }
}

function writeToStorage(next: InternalAdminToolsPreferences) {
  if (typeof window === 'undefined') return
  try {
    if (Object.keys(next).length === 0) window.localStorage.removeItem(STORAGE_KEY)
    else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // localStorage unavailable (private mode, quota) — the preference stays in memory
  }
}

let preferences = readFromStorage()
const listeners = new Set<() => void>()

function notify() {
  listeners.forEach((listener) => listener())
}

/**
 * Replaced wholesale on every write so the reference doubles as the
 * `useSyncExternalStore` snapshot.
 */
function commit(next: InternalAdminToolsPreferences) {
  preferences = next
  writeToStorage(next)
  notify()
}

/** Another tab of the same browser flipped the switch: adopt its value. */
function onStorage(event: StorageEvent) {
  if (event.key !== null && event.key !== STORAGE_KEY) return
  preferences = readFromStorage()
  notify()
}

export function getInternalAdminToolsPreferences(): InternalAdminToolsPreferences {
  return preferences
}

/** No signed-in user means no preference, so nobody sees the tools by accident. */
export function isInternalAdminToolsEnabled(
  prefs: InternalAdminToolsPreferences,
  userId: string | null | undefined
): boolean {
  return !!userId && prefs[userId] === true
}

export function subscribeToInternalAdminTools(listener: () => void): () => void {
  listeners.add(listener)
  if (listeners.size === 1 && typeof window !== 'undefined') window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0 && typeof window !== 'undefined') window.removeEventListener('storage', onStorage)
  }
}

export function setInternalAdminToolsEnabled(userId: string, enabled: boolean) {
  if (isInternalAdminToolsEnabled(preferences, userId) === enabled) return
  const next = { ...preferences }
  if (enabled) next[userId] = true
  else delete next[userId]
  commit(next)
}

/** Test seam: forget every preference, in memory and in storage. */
export function clearInternalAdminToolsPreferences() {
  if (Object.keys(preferences).length === 0) return
  commit(EMPTY)
}
